import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./styles/dashboard.css";
import "./styles/mis-dashboard.css";
import toast from "react-hot-toast";
import CommonHeader from "./components/CommonHeader";

function monthStartIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatInt(n) {
  return (Number(n) || 0).toLocaleString("en-AE");
}

function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function ChangeBadge({ value, invert = false }) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const upIsGood = !invert;
  const positive = n > 0;
  const negative = n < 0;
  let tone = "neutral";
  if (positive) tone = upIsGood ? "good" : "bad";
  if (negative) tone = upIsGood ? "bad" : "good";
  const arrow = n > 0 ? "▲" : n < 0 ? "▼" : "•";
  return (
    <span className={`mis-change mis-change--${tone}`}>
      {arrow} {Math.abs(n)}%
    </span>
  );
}

function KpiCard({ label, value, sub, change, invertChange, accent }) {
  return (
    <div className={`mis-kpi mis-kpi--${accent || "default"}`}>
      <div className="mis-kpi__label">{label}</div>
      <div className="mis-kpi__value">{value}</div>
      <div className="mis-kpi__foot">
        {sub ? <span className="mis-kpi__sub">{sub}</span> : <span />}
        <ChangeBadge value={change} invert={invertChange} />
      </div>
    </div>
  );
}

function BarList({ items, valueKey = "total", labelKey = "name", maxItems = 10, formatValue }) {
  const rows = (items || []).slice(0, maxItems);
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  const fmt = formatValue || ((v) => formatInt(v));

  if (rows.length === 0) {
    return <p className="mis-empty">No data for this period.</p>;
  }

  return (
    <ul className="mis-bars">
      {rows.map((row, idx) => {
        const val = Number(row[valueKey]) || 0;
        const pct = Math.round((val / max) * 100);
        const color = row.color_code || undefined;
        return (
          <li key={`${row[labelKey]}-${idx}`} className="mis-bars__row">
            <div className="mis-bars__meta">
              <span className="mis-bars__label" title={row[labelKey]}>
                {row[labelKey]}
              </span>
              <span className="mis-bars__value">{fmt(val)}</span>
            </div>
            <div className="mis-bars__track">
              <div
                className="mis-bars__fill"
                style={{
                  width: `${pct}%`,
                  ...(color ? { background: color } : {}),
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TrendChart({ trend }) {
  const points = trend || [];
  const maxActive = Math.max(...points.map((p) => Number(p.active_bookings) || 0), 1);
  const maxRevenue = Math.max(...points.map((p) => Number(p.revenue) || 0), 1);

  if (points.length === 0) {
    return <p className="mis-empty">No daily trend data.</p>;
  }

  const w = 640;
  const h = 180;
  const padX = 8;
  const padY = 16;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;
  const n = points.length;

  const xAt = (i) => padX + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yActive = (v) => padY + innerH - (v / maxActive) * innerH;
  const yRev = (v) => padY + innerH - (v / maxRevenue) * innerH;

  const activePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yActive(Number(p.active_bookings) || 0).toFixed(1)}`)
    .join(" ");
  const revenuePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yRev(Number(p.revenue) || 0).toFixed(1)}`)
    .join(" ");

  return (
    <div className="mis-trend">
      <svg viewBox={`0 0 ${w} ${h}`} className="mis-trend__svg" preserveAspectRatio="none">
        <line x1={padX} y1={h - padY} x2={w - padX} y2={h - padY} className="mis-trend__axis" />
        <path d={activePath} className="mis-trend__line mis-trend__line--active" />
        <path d={revenuePath} className="mis-trend__line mis-trend__line--revenue" />
        {points.map((p, i) => (
          <circle
            key={p.report_date}
            cx={xAt(i)}
            cy={yActive(Number(p.active_bookings) || 0)}
            r={n > 45 ? 1.5 : 2.5}
            className="mis-trend__dot"
          />
        ))}
      </svg>
      <div className="mis-trend__legend">
        <span>
          <i className="mis-dot mis-dot--active" /> Active bookings
        </span>
        <span>
          <i className="mis-dot mis-dot--revenue" /> Revenue (scaled)
        </span>
      </div>
      <div className="mis-trend__x">
        <span>{formatShortDate(points[0].report_date)}</span>
        {n > 2 ? <span>{formatShortDate(points[Math.floor(n / 2)].report_date)}</span> : null}
        <span>{formatShortDate(points[n - 1].report_date)}</span>
      </div>
    </div>
  );
}

const MisDashboard = () => {
  const [fromDate, setFromDate] = useState(monthStartIso);
  const [toDate, setToDate] = useState(todayIso);
  const [dateBasis, setDateBasis] = useState("booking_date");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const loadSummary = useCallback(async (opts = {}) => {
    const from = opts.fromDate ?? fromDate;
    const to = opts.toDate ?? toDate;
    const basis = opts.dateBasis ?? dateBasis;

    if (!from || !to) {
      toast.error("From date and To date are required.");
      return;
    }

    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("from_date", from);
      body.append("to_date", to);
      body.append("date_basis", basis);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/misDashboard/getSummary`,
        {
          method: "POST",
          headers: { Authorization: localStorage.getItem("token") },
          body,
        }
      );
      const result = await response.json();
      if (result.status === 1 || result.message === "OK") {
        setData(result);
      } else {
        toast.error(result.message || "Failed to load dashboard");
        setData(null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dashboard");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, dateBasis]);

  useEffect(() => {
    loadSummary();
    // Initial load only — intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const f = start.toISOString().slice(0, 10);
    const t = end.toISOString().slice(0, 10);
    setFromDate(f);
    setToDate(t);
    loadSummary({ fromDate: f, toDate: t });
  };

  const setThisMonth = () => {
    const f = monthStartIso();
    const t = todayIso();
    setFromDate(f);
    setToDate(t);
    loadSummary({ fromDate: f, toDate: t });
  };

  const basisLabel = dateBasis === "pickup_date" ? "Pickup (travel) date" : "Booking (sales) date";

  const comparisonNote = useMemo(() => {
    if (!data?.comparison_period) return null;
    const { from_date, to_date } = data.comparison_period;
    return `vs previous period ${formatShortDate(from_date)} – ${formatShortDate(to_date)}`;
  }, [data]);

  const kpis = data?.kpis;

  return (
    <div className="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
      <div className="h-screen flex-grow-1 overflow-y-lg-auto">
        <CommonHeader />
        <main className="py-6 bg-surface-secondary">
          <div className="container-fluid mis-dashboard">
            <div className="mis-hero">
              <div>
                <h1 className="mis-hero__title">MIS Dashboard</h1>
                <p className="mis-hero__sub">
                  Business overview across bookings, revenue, agents, routes, and operations.
                  {comparisonNote ? (
                    <>
                      {" "}
                      <span className="mis-hero__compare">{comparisonNote}</span>
                    </>
                  ) : null}
                </p>
              </div>
            </div>

            <div className="card shadow border-0 mb-4 mis-filters">
              <div className="card-body">
                <div className="mis-filters__row">
                  <div className="mis-field">
                    <label htmlFor="mis_from">From</label>
                    <input
                      id="mis_from"
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="mis-field">
                    <label htmlFor="mis_to">To</label>
                    <input
                      id="mis_to"
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="mis-field">
                    <label htmlFor="mis_basis">Date basis</label>
                    <select
                      id="mis_basis"
                      className="form-select"
                      value={dateBasis}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDateBasis(v);
                        loadSummary({ dateBasis: v });
                      }}
                    >
                      <option value="booking_date">Booking date (sales)</option>
                      <option value="pickup_date">Pickup date (travel)</option>
                    </select>
                  </div>
                  <div className="mis-field mis-field--actions">
                    <label>&nbsp;</label>
                    <div className="mis-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setPreset(7)}
                      >
                        7d
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setPreset(30)}
                      >
                        30d
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={setThisMonth}
                      >
                        MTD
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={loading}
                        onClick={() => loadSummary()}
                      >
                        {loading ? "Loading…" : "Apply"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mis-filters__hint">
                  Measuring by <strong>{basisLabel}</strong>. % changes compare to the equal-length prior period.
                </div>
              </div>
            </div>

            {loading && !data ? (
              <div className="mis-loading">Loading dashboard…</div>
            ) : null}

            {kpis ? (
              <>
                <div className="mis-kpi-grid">
                  <KpiCard
                    label="Total bookings"
                    value={formatInt(kpis.total_bookings)}
                    sub={`${formatInt(kpis.active_bookings)} active`}
                    change={kpis.changes?.total_bookings}
                    accent="slate"
                  />
                  <KpiCard
                    label="Revenue (active)"
                    value={formatMoney(kpis.revenue)}
                    sub={`Avg ${formatMoney(kpis.avg_amount)} / booking`}
                    change={kpis.changes?.revenue}
                    accent="teal"
                  />
                  <KpiCard
                    label="Cancelled"
                    value={formatInt(kpis.cancelled_bookings)}
                    sub={`Cancel rate ${kpis.cancel_rate}%`}
                    change={kpis.changes?.cancelled_bookings}
                    invertChange
                    accent="rose"
                  />
                  <KpiCard
                    label="Boarding rate"
                    value={`${kpis.boarding_rate}%`}
                    sub={`${formatInt(kpis.boarded)} boarded · ${formatInt(kpis.not_boarded)} open`}
                    change={kpis.changes?.boarding_rate}
                    accent="amber"
                  />
                </div>

                <div className="mis-grid mis-grid--2">
                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Daily trend</h2>
                      <span>Active volume &amp; revenue shape</span>
                    </div>
                    <div className="card-body">
                      <TrendChart trend={data.daily_trend} />
                    </div>
                  </section>

                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Top agents</h2>
                      <span>By active bookings</span>
                    </div>
                    <div className="card-body">
                      {(data.top_agents || []).length === 0 ? (
                        <p className="mis-empty">No agent data.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm mis-table mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Agency</th>
                                <th className="text-end">Active</th>
                                <th className="text-end">Cancel</th>
                                <th className="text-end">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.top_agents.map((a, i) => (
                                <tr key={a.agency_id}>
                                  <td>{i + 1}</td>
                                  <td>{a.agency_name}</td>
                                  <td className="text-end">{formatInt(a.bookings)}</td>
                                  <td className="text-end">{formatInt(a.cancelled)}</td>
                                  <td className="text-end">{formatMoney(a.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="mis-grid mis-grid--3">
                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Pickup points</h2>
                      <span>Origin demand</span>
                    </div>
                    <div className="card-body">
                      <BarList items={data.pickup_points} />
                    </div>
                  </section>

                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Drop points</h2>
                      <span>Destination demand</span>
                    </div>
                    <div className="card-body">
                      <BarList items={data.drop_points} />
                    </div>
                  </section>

                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Bus slots</h2>
                      <span>Slot load (active)</span>
                    </div>
                    <div className="card-body">
                      <BarList items={data.bus_slots} />
                    </div>
                  </section>
                </div>

                <div className="mis-grid mis-grid--3">
                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Top corridors</h2>
                      <span>Pickup → drop</span>
                    </div>
                    <div className="card-body">
                      <BarList items={data.corridors} />
                    </div>
                  </section>

                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Follow-up status</h2>
                      <span>Ops funnel (active)</span>
                    </div>
                    <div className="card-body">
                      <BarList items={data.follow_up} />
                    </div>
                  </section>

                  <section className="card shadow border-0 mis-panel">
                    <div className="card-header mis-panel__head">
                      <h2>Staff entries</h2>
                      <span>Bookings created by user</span>
                    </div>
                    <div className="card-body">
                      {(data.staff || []).length === 0 ? (
                        <p className="mis-empty">No staff data.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm mis-table mb-0">
                            <thead>
                              <tr>
                                <th>User</th>
                                <th className="text-end">Total</th>
                                <th className="text-end">Active</th>
                                <th className="text-end">Cancel</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.staff.map((s) => (
                                <tr key={s.name}>
                                  <td>{s.name}</td>
                                  <td className="text-end">{formatInt(s.total)}</td>
                                  <td className="text-end">{formatInt(s.active)}</td>
                                  <td className="text-end">{formatInt(s.cancelled)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </>
            ) : null}

            {!loading && !data ? (
              <div className="mis-empty-state">
                Choose a date range and click Apply to load insights.
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MisDashboard;
